import { IconButton, Link, Stack, SxProps, Theme } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { Tooltip } from 'src/components';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormAutocompleteFieldValue, FormField, getFormField } from 'src/components/Forms';
import { FormLabel } from 'src/components/Forms/FormLabel/form-label';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { DocumentationFormField } from './site-documentation.types';

// These rows keep their old look: the value pinned to the row's right edge, the placeholder dimmed.
const valueCellSx: SxProps<Theme> = {
  alignItems: 'flex-end',
  '& [data-placeholder]': {
    color: (theme: Theme) => theme.palette.custom.OV.sys.outline,
  },
};

const aiSuggestionFieldSx: SxProps<Theme> = {
  '&:hover': {
    '--hover-bg': (theme: Theme) =>
      `color-mix(in srgb, ${theme.palette.custom.OV.sys.aiSuggestionsTextBackground} ${theme.palette.mode === 'dark' ? '70%' : '97%'}, black)`,
    backgroundColor: 'var(--hover-bg)',
    '& button': { backgroundColor: 'transparent' },
  },
};

interface SubSystemFieldProps {
  field: DocumentationFormField;
  disabled?: boolean;
  textFieldProps?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
  onFieldChange: (fieldName: string, value: string | null | boolean) => void;
  onResetField: (fieldName: string) => void;
  hasAISuggestion?: boolean;
  link?: string;
}

export const SubSystemField = ({
  field,
  disabled,
  textFieldProps,
  inputProps,
  onFieldChange,
  onResetField,
  hasAISuggestion,
  link,
}: SubSystemFieldProps): ReactElement => {
  const innerSx = useMemo(
    () => ({
      ...(hasAISuggestion && {
        backgroundColor: (theme: Theme) =>
          theme.palette.custom.OV.sys.aiSuggestionsTextBackground,
      }),
    }),
    [hasAISuggestion],
  );

  const fieldProps = useMemo<Partial<FormField> & { wrapperSx?: SxProps }>(() => {
    switch (true) {
      case field.type === 'rich-text-bubble':
        return {
          type: 'rich-text-bubble' as const,
          wrapperSx: {
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
          name: field.name,
          placeholder: field.placeholder,
          debounced: field.debounced || SAVE_DEBOUNCE_DELAY,
          sx: { width: '100%' },
          watchContent: true,
          onChange: (value: string) => onFieldChange(field.name, value),
        };
      case field.type === 'switch':
        return {
          type: 'switch' as const,
          name: field.name,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange(field.name, event.target.checked),
        };
      case !!field.options?.length: {
        const mapSelectionToValue = (value: unknown) => {
          if (typeof value === 'string') {
            return value;
          }

          if (Array.isArray(value)) {
            return null;
          }

          if (value && (value as { id?: unknown }).id !== undefined) {
            return String((value as { id: unknown }).id);
          }

          return null;
        };

        return {
          type: 'autocomplete' as const,
          name: field.name,
          options: field.options ?? [],
          inline: true,
          menuWidth: '15rem',
          placeholder: 'Select Option',
          sx: hasAISuggestion ? [valueCellSx, aiSuggestionFieldSx] : valueCellSx,
          // Clearing saves '' like the blank option this menu used to have, not null.
          onChange: (value: FormAutocompleteFieldValue) =>
            onFieldChange(field.name, value === null ? '' : mapSelectionToValue(value)),
        };
      }
      default:
        return {
          type: 'textfield' as const,
          name: field.name,
          onChangeDebounceDelay: SAVE_DEBOUNCE_DELAY,
          pattern: field.pattern,
          textFieldProps: {
            ...textFieldProps,
            InputProps: inputProps,
            placeholder: 'Enter Value',
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              onFieldChange(field.name, event.target.value),
          },
        };
    }
  }, [
    field.debounced,
    field.name,
    field.options,
    field.pattern,
    field.placeholder,
    field.type,
    hasAISuggestion,
    inputProps,
    onFieldChange,
    textFieldProps,
  ]);

  const { wrapperSx, ...restFieldProps } = fieldProps;

  return (
    <Stack direction="row" alignItems="center" gap={1} key={field.name}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flexGrow={1}
        pl={2}
        pr={1}
        borderRadius={1}
        sx={{ ...innerSx, ...wrapperSx }}
      >
        <FormLabel
          label={field.label}
          sx={{
            ...(hasAISuggestion && { color: 'inherit' }),
            width: 'auto',
            paddingY: 1,
            overflow: 'visible',
            textWrap: 'wrap',
          }}
        />
        <Stack direction="row" width="100%" alignItems="center" justifyContent="end">
          {getFormField(restFieldProps as FormField, disabled)}

          {!!link && (
            <Tooltip
              title={
                <Stack direction="column" alignItems="center">
                  <span>{link}</span>
                  <span>Click to open</span>
                </Stack>
              }
            >
              <Link
                href={link}
                underline="none"
                color="inherit"
                target="_blank"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <DynamicIcon name="arrow-up-right" library="lucide" size={19} />
              </Link>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {!!hasAISuggestion && (
        <IconButton
          onClick={() => onResetField(field.name)}
          size="small"
          color="primary"
          title="Reset to original"
        >
          <DynamicIcon name="rotate-cw" library="lucide" />
        </IconButton>
      )}
    </Stack>
  );
};
